import { DEFAULT_TUNING_RANGE_PERCENT } from './constants';
import { OptimizationSettings } from './types';

/** What the optimizer does with one reference flavor. */
export type FlavorMode = 'free' | 'lock' | 'remove' | 'tune';

export interface FlavorToken {
  flavor: string;
  label: string;
  mode: FlavorMode;
  /** The target product's decomp share of this flavor, in percent; absent when it has none. */
  sharePercent?: number;
  /** In the parent report's reference flavors, so the optimizer can act on it. */
  inReport: boolean;
}

export interface LexiconFlavor {
  flavor: string;
}

type FlavorLists = Pick<
  OptimizationSettings,
  'refFlavorsToLock' | 'refFlavorsToRemove' | 'refFlavorsToTune'
>;

/**
 * A lexicon name as the optimizer's reference flavor key, e.g. "Crème fraîche"
 * → `creme_fraiche`, "Buddha's Hand" → `buddhas_hand`. Mirrors the cleaning the
 * R pipeline applies; matches 98% of lexicon names to keys in real reports.
 */
export const flavorKey = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9&]+/g, '_')
    .replace(/^_+|_+$/g, '');

/** R's per-GGVar `generic_*` aggregates are not real flavors and are never optimized. */
const isGeneric = (flavor: string) => flavor.startsWith('generic_');

/**
 * Without new reference flavors, the optimizer pins every flavor whose decomp
 * share is not above 1% to 0 (`GG_Genetic-Algorithms.R`, `Decomp > 0.01`).
 */
export const NEW_FLAVOR_CUTOFF = 0.01;

const round2 = (n: number) => Math.round(n * 100) / 100;

type LockAndTune = Pick<
  OptimizationSettings,
  'refFlavorsToLock' | 'refFlavorsToTune'
>;

const shareLookup = (shares: ReadonlyMap<string, number>) => {
  const byKey = new Map(
    Array.from(shares, ([flavor, share]) => [flavor.toLowerCase(), share]),
  );
  return (flavor: string) => byKey.get(flavor.toLowerCase()) ?? 0;
};

/**
 * Locked and tuned flavors missing from the parent report. The optimizer only
 * searches the report's flavors and drops tune ranges for any other, so these
 * can't be honoured whether or not new flavors are allowed.
 * Empty while the report's flavors are unknown.
 */
export const flavorsNotInReport = (
  opt: LockAndTune,
  reportFlavors: readonly string[],
): string[] => {
  if (reportFlavors.length === 0) return [];
  const known = new Set(reportFlavors.map((f) => f.toLowerCase()));
  return [
    ...opt.refFlavorsToLock,
    ...opt.refFlavorsToTune.map((t) => t.flavor),
  ].filter((f) => !known.has(f.toLowerCase()));
};

/**
 * Locked and tuned flavors the optimizer can't honour when new flavors are not
 * allowed: the product has at most 1% of them, so they are held at 0%. A lock
 * still holds when it rounds to 0%, and so does a tune that allows 0%.
 * Flavors missing from the parent report are left to `flavorsNotInReport`.
 * Empty when the product's shares are unknown.
 */
export const flavorsNeedingNewFlavors = (
  opt: LockAndTune,
  shares: ReadonlyMap<string, number>,
  reportFlavors: readonly string[] = [],
): string[] => {
  if (shares.size === 0) return [];
  const shareOf = shareLookup(shares);
  const notInReport = new Set(flavorsNotInReport(opt, reportFlavors));
  const pinned = (flavor: string) =>
    !notInReport.has(flavor) && shareOf(flavor) <= NEW_FLAVOR_CUTOFF;
  return [
    ...opt.refFlavorsToLock.filter((f) => pinned(f) && round2(shareOf(f)) > 0),
    ...opt.refFlavorsToTune
      .filter((t) => pinned(t.flavor) && t.min > 0)
      .map((t) => t.flavor),
  ];
};

export const flavorLabel = (flavor: string) => flavor.replace(/_/g, ' ');

/** "a", "a and b", "a, b and c", with underscores shown as spaces. */
export const listFlavors = (flavors: string[]) => {
  const labels = flavors.map(flavorLabel);
  return labels.length <= 1
    ? labels.join('')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

/** Why the existing-flavors run holds `flavors` (from `flavorsNeedingNewFlavors`) at 0%. */
export const newFlavorsMessage = (
  flavors: string[],
  shares: ReadonlyMap<string, number>,
): string => {
  const shareOf = shareLookup(shares);
  const absent = flavors.filter((f) => shareOf(f) <= 0);
  const low = flavors.filter((f) => shareOf(f) > 0);
  const clauses = [
    absent.length > 0 &&
      `${listFlavors(absent)} ${absent.length === 1 ? "isn't" : "aren't"} in this product`,
    low.length > 0 &&
      `${listFlavors(low)} ${low.length === 1 ? 'is' : 'are'} at most 1% of ${absent.length > 0 ? 'it' : 'this product'}`,
  ].filter(Boolean);
  return `${clauses.join(' and ')}, so the optimizer keeps ${flavors.length === 1 ? 'it' : 'them'} at 0% unless new flavors are allowed`;
};

export const notInReportMessage = (flavors: string[]): string => {
  const one = flavors.length === 1;
  return `${listFlavors(flavors)} ${one ? "isn't" : "aren't"} in the parent report, so the optimizer can't lock or tune ${one ? 'it' : 'them'}, even with new flavors allowed`;
};

export const tokenMode = (opt: FlavorLists, flavor: string): FlavorMode => {
  if (opt.refFlavorsToLock.includes(flavor)) return 'lock';
  if (opt.refFlavorsToRemove.includes(flavor)) return 'remove';
  if (opt.refFlavorsToTune.some((t) => t.flavor === flavor)) return 'tune';
  return 'free';
};

/** Puts each flavor in at most one of lock / remove / tune, so the lists can never conflict. */
export const withTokenModes = (
  opt: FlavorLists,
  flavors: string[],
  mode: FlavorMode,
): FlavorLists => {
  const target = new Set(flavors);
  const keep = (f: string) => !target.has(f);
  const tune = opt.refFlavorsToTune.filter((t) => keep(t.flavor));
  const [min, max] = DEFAULT_TUNING_RANGE_PERCENT;
  return {
    refFlavorsToLock: [
      ...opt.refFlavorsToLock.filter(keep),
      ...(mode === 'lock' ? flavors : []),
    ],
    refFlavorsToRemove: [
      ...opt.refFlavorsToRemove.filter(keep),
      ...(mode === 'remove' ? flavors : []),
    ],
    refFlavorsToTune:
      mode === 'tune'
        ? [
            ...tune,
            ...flavors.map(
              (flavor) =>
                opt.refFlavorsToTune.find((t) => t.flavor === flavor) ?? {
                  flavor,
                  min,
                  max,
                },
            ),
          ]
        : tune,
  };
};

interface PoolInput {
  opt: FlavorLists;
  /** The parent report's reference flavors. */
  reportFlavors: string[];
  /** Target product decomp share per flavor, as a 0–1 fraction. */
  shares: ReadonlyMap<string, number>;
  lexicon: LexiconFlavor[];
  /** Offer every report and lexicon flavor, not just the product's decomp flavors. */
  offerAll?: boolean;
}

const constrainedFlavors = (opt: FlavorLists) => [
  ...opt.refFlavorsToLock,
  ...opt.refFlavorsToRemove,
  ...opt.refFlavorsToTune.map((t) => t.flavor),
];

/**
 * Every flavor that can be constrained: constrained ones (so a cloned choice
 * never disappears), the product's decomp flavors and, with `offerAll`, the
 * report's and the lexicon's. Sorted A–Z.
 * `reportFlavors` is always needed to tell which flavors are in the report.
 */
export const buildTokenPool = ({
  opt,
  reportFlavors,
  shares,
  lexicon,
  offerAll = true,
}: PoolInput): FlavorToken[] => {
  // Keys can differ only in case between sources (e.g. `Citrus` vs the lexicon's `citrus`);
  // the first spelling seen wins, so report and constrained spellings beat the lexicon's.
  const canonical = new Map<string, string>();
  const add = (flavor: string) => {
    const key = flavor.toLowerCase();
    if (!canonical.has(key)) canonical.set(key, flavor);
  };
  const inReport = new Set(reportFlavors.map((f) => f.toLowerCase()));
  constrainedFlavors(opt).forEach(add);
  shares.forEach(
    (share, flavor) => share > 0 && !isGeneric(flavor) && add(flavor),
  );
  if (offerAll) {
    reportFlavors.filter((f) => !isGeneric(f)).forEach(add);
    lexicon.forEach((l) => add(l.flavor));
  }

  const tokens = Array.from(
    canonical.entries(),
    ([key, flavor]): FlavorToken => {
      const share = shares.get(flavor);
      return {
        flavor,
        // One spelling per flavor in every view, so names never change as the search widens.
        label: flavorLabel(flavor),
        mode: tokenMode(opt, flavor),
        sharePercent:
          share && share > 0 ? Math.round(share * 1000) / 10 : undefined,
        inReport: inReport.has(key),
      };
    },
  );

  return tokens.sort((a, b) => a.label.localeCompare(b.label));
};
